'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function MemberContact() {
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    setLoading(true);
    // Backend integration point: POST /api/member/mariasantos/contact
    await new Promise(r => setTimeout(r, 1000));
    toast.success('Message sent to Maria!', {
      description: 'She typically responds within 24 hours.',
    });
    reset();
    setLoading(false);
  };

  return (
    <section id="contact" className="py-16 lg:py-24 bg-secondary">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <span className="section-divider" />
              <span className="text-xs font-semibold tracking-widest uppercase text-primary">Get in Touch</span>
            </div>
            <h2 className="font-display text-section-title text-foreground mb-4">
              Contact Maria
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Have questions about a product? Want to know more about the Richésse opportunity? Maria personally responds to every message.
            </p>

            <div className="space-y-4">
              {[
                { id: 'contact-resp', icon: 'ClockIcon', title: 'Response Time', desc: 'Typically within 24 hours' },
                { id: 'contact-lang', icon: 'GlobeAltIcon', title: 'Languages', desc: 'English, Filipino, Spanish' },
                { id: 'contact-avail', icon: 'CalendarIcon', title: 'Availability', desc: 'Mon–Sat, 8AM–8PM PHT' },
              ].map(c => (
                <div key={c.id} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name={c.icon as any} size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{c.title}</p>
                    <p className="text-xs text-muted-foreground">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="card-elevated rounded-2xl p-6 lg:p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="richesse-label">Your Name *</label>
                    <input
                      type="text"
                      placeholder="Andrea Garcia"
                      className={`richesse-input ${errors.name ? 'border-danger' : ''}`}
                      {...register('name', { required: 'Name is required' })}
                    />
                    {errors.name && <p className="mt-1 text-xs text-danger">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label className="richesse-label">Email Address *</label>
                    <input
                      type="email"
                      placeholder="andrea@example.com"
                      className={`richesse-input ${errors.email ? 'border-danger' : ''}`}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
                      })}
                    />
                    {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
                  </div>
                </div>

                <div>
                  <label className="richesse-label">Subject *</label>
                  <select
                    className={`richesse-input ${errors.subject ? 'border-danger' : ''}`}
                    {...register('subject', { required: 'Please select a subject' })}
                  >
                    <option value="">Select a topic...</option>
                    <option value="product-question">Product Question</option>
                    <option value="order-support">Order Support</option>
                    <option value="membership">Richésse Membership</option>
                    <option value="opportunity">Business Opportunity</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.subject && <p className="mt-1 text-xs text-danger">{errors.subject.message}</p>}
                </div>

                <div>
                  <label className="richesse-label">Message *</label>
                  <textarea
                    rows={5}
                    placeholder="Hi Maria, I'd love to know more about..."
                    className={`richesse-input resize-none ${errors.message ? 'border-danger' : ''}`}
                    {...register('message', {
                      required: 'Message is required',
                      minLength: { value: 20, message: 'Please write at least 20 characters' },
                    })}
                  />
                  {errors.message && <p className="mt-1 text-xs text-danger">{errors.message.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
                  style={{ minHeight: '48px' }}
                >
                  {loading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message to Maria
                      <Icon name="PaperAirplaneIcon" size={16} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}